// 使用运维平台专用的请求工具，确保与用户界面完全隔离
import opsRequest from '@/utils/opsRequest';

/**
 * 运维管理相关 API
 */
export const opsApi = {
  // ========== 管理功能 ==========
  
  // 1. 用户管理
  // 获取所有用户列表
  getAllUsers(params) {
    return opsRequest.get('/ops/users', { params });
  },
  
  // 获取用户详情
  getUserDetail(userId) {
    return opsRequest.get(`/ops/users/${userId}`);
  },
  
  // 更新用户信息
  updateUser(userId, userData) {
    return opsRequest.put(`/ops/users/${userId}`, userData);
  },
  
  // 启用/禁用用户
  toggleUserStatus(userId, status) {
    return opsRequest.post(`/ops/users/${userId}/status`, { status });
  },
  
  // 删除用户（单个）
  deleteUser(userId) {
    return opsRequest.delete(`/ops/users/${userId}`);
  },
  
  // 批量删除用户
  batchDeleteUsers(userIds) {
    return opsRequest.post('/ops/users/batch-delete', { userIds });
  },
  
  // 获取父账户信息
  getParentAccount(userId) {
    return opsRequest.get(`/ops/users/${userId}/parent`);
  },
  
  // 获取子账户列表
  getSubAccounts(userId) {
    return opsRequest.get(`/ops/users/${userId}/sub-accounts`);
  },
  
  // 2. 文件管理
  // 获取用户上传文件列表
  getUserFiles(params) {
    return opsRequest.get('/ops/files', { params });
  },
  
  // 标记文件为已解决
  markFileResolved(fileId) {
    return opsRequest.post(`/ops/files/${fileId}/resolve`);
  },
  // 切换文件解决状态
  toggleFileResolved(fileId) {
    return opsRequest.post(`/ops/files/${fileId}/toggle-resolved`);
  },
  
  // 3. 操作日志
  // 获取用户操作日志
  getUserLogs(params) {
    return opsRequest.get('/ops/logs', { params });
  },
  
  // 4. 新增OP
  // 创建运维管理员
  createOp(opData) {
    return opsRequest.post('/ops/create-op', opData);
  },
  
  // ========== 运行维护功能 ==========
  
  // 1. HTTP/TCP监控
  // 获取在线用户列表
  getOnlineUsers() {
    return opsRequest.get('/ops/monitor/online-users');
  },
  
  // 获取访问统计
  getAccessStats(params) {
    return opsRequest.get('/ops/monitor/access-stats', { params });
  },
  
  // 2. MySQL监控
  // 获取MySQL监控数据
  getMysqlMonitor() {
    return opsRequest.get('/ops/monitor/mysql');
  },
  
  // 获取MySQL慢查询日志
  getMysqlSlowQueries(params) {
    return opsRequest.get('/ops/monitor/mysql/slow-queries', { params });
  },
  
  // 3. 主机信息统计
  // 获取主机列表
  getHosts() {
    return opsRequest.get('/ops/monitor/hosts');
  },
  
  // 获取主机详情
  getHostDetail(hostId) {
    return opsRequest.get(`/ops/monitor/hosts/${hostId}`);
  },
  
  // 4. 磁盘监控
  // 获取磁盘监控数据
  getDiskMonitor(hostId) {
    if (hostId) {
      return opsRequest.get(`/ops/monitor/disk/${hostId}`);
    }
    return opsRequest.get('/ops/monitor/disk');
  },
  
  // 更新磁盘报警阈值
  updateDiskThreshold(hostId, threshold) {
    return opsRequest.post(`/ops/monitor/disk/${hostId}/threshold`, { threshold });
  },
  
  // 5. 磁盘IO监控
  // 获取磁盘IO数据
  getDiskIO(hostId) {
    if (hostId) {
      return opsRequest.get(`/ops/monitor/disk-io/${hostId}`);
    }
    return opsRequest.get('/ops/monitor/disk-io');
  },
  
  // 更新磁盘IO报警阈值
  updateDiskIOThreshold(hostId, threshold) {
    return opsRequest.post(`/ops/monitor/disk-io/${hostId}/threshold`, { threshold });
  },
  
  // 6. 内存监控
  // 获取内存监控数据
  getMemoryMonitor(hostId) {
    if (hostId) {
      return opsRequest.get(`/ops/monitor/memory/${hostId}`);
    }
    return opsRequest.get('/ops/monitor/memory');
  },
  
  // 更新内存报警阈值
  updateMemoryThreshold(hostId, threshold) {
    return opsRequest.post(`/ops/monitor/memory/${hostId}/threshold`, { threshold });
  },
  
  // 7. CPU负载监控
  // 获取CPU负载数据
  getCpuLoad(hostId) {
    if (hostId) {
      return opsRequest.get(`/ops/monitor/cpu/${hostId}`);
    }
    return opsRequest.get('/ops/monitor/cpu');
  },
  
  // 更新CPU负载报警阈值
  updateCpuThreshold(hostId, threshold) {
    return opsRequest.post(`/ops/monitor/cpu/${hostId}/threshold`, { threshold });
  },
  
  // 更新网络流量报警阈值
  updateNetworkThreshold(hostId, threshold) {
    return opsRequest.post(`/ops/monitor/network/${hostId}/threshold`, { threshold });
  },
  
  // 获取监控历史数据
  getMonitorHistory(metricType, hostId, hours) {
    return opsRequest.get(`/ops/monitor/history/${metricType}`, {
      params: { hostId, hours }
    });
  },
  
  // 获取报警阈值
  getAlarmThreshold(metricType, hostId) {
    return opsRequest.get(`/ops/monitor/threshold/${metricType}`, {
      params: { hostId }
    });
  },
  
  // 更新报警阈值（通用）
  updateAlarmThreshold(metricType, hostId, threshold, unit) {
    return opsRequest.post(`/ops/monitor/threshold/${metricType}`, {
      threshold,
      unit
    }, {
      params: { hostId }
    });
  },
  
  // ========== 反馈管理 ==========
  // 获取反馈列表
  getFeedbackList(params) {
    return opsRequest.get('/feedback/list', { params });
  },
  
  // 更新反馈状态
  updateFeedbackStatus(feedbackId, status, adminRemark = null) {
    const payload = { status };
    // 如果 adminRemark 不为 null 或 undefined，就添加到 payload 中（包括空字符串）
    if (adminRemark !== null && adminRemark !== undefined) {
      payload.adminRemark = adminRemark;
    }
    return opsRequest.post(`/feedback/${feedbackId}/status`, payload);
  },
  
  // 删除反馈
  deleteFeedback(feedbackId) {
    return opsRequest.delete(`/feedback/${feedbackId}`);
  }
};

